import { useEffect, useMemo, useState, useRef } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, Clock, Globe, AlertCircle, Mail, User as UserIcon } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  COMMON_TIMEZONES, userBrowserTimezone, tzLabelWithOffset,
  formatLongDateInTz, formatTime12hInTz,
} from "@/lib/time";

// --- Calendar helpers (inlined — intentionally duplicated from
//     PublicBookingV2.jsx so the booking calendar file stays untouched).
function startOfMonth(d) {
  const x = new Date(d); x.setDate(1); x.setHours(0, 0, 0, 0); return x;
}
function addMonths(d, n) {
  const x = new Date(d); x.setMonth(x.getMonth() + n); return x;
}
function isoDay(d, tz) {
  return new Intl.DateTimeFormat("en-CA", { timeZone: tz, year: "numeric", month: "2-digit", day: "2-digit" }).format(d);
}
function buildMonthGrid(viewDate, tz) {
  const first = new Date(viewDate.getFullYear(), viewDate.getMonth(), 1);
  const startWeekday = first.getDay();
  const days = [];
  for (let i = 0; i < 42; i++) {
    const d = new Date(first);
    d.setDate(first.getDate() - startWeekday + i);
    days.push({ date: d, key: isoDay(d, tz), inMonth: d.getMonth() === viewDate.getMonth() });
  }
  return days;
}
function formatDuration(mins) {
  const m = Math.max(0, Number(mins) || 0);
  if (m < 60) return `${m} min`;
  const h = Math.floor(m / 60);
  const rem = m % 60;
  const hLabel = h === 1 ? "1 hr" : `${h} hrs`;
  return rem ? `${hLabel} ${rem} min` : hLabel;
}

// Logo-free white shell that matches PublicShell in PublicBookingV2.jsx
// pixel-for-pixel so the reschedule page sits in the same visual frame
// as the public booking page.
function PublicShell({ children }) {
  return (
    <div className="min-h-screen w-full bg-white pt-6 pb-8 px-4">
      {children}
    </div>
  );
}

export default function BookingManage() {
  const { token } = useParams();
  const [searchParams] = useSearchParams();
  const action = searchParams.get("action") || "cancel";

  const [info, setInfo] = useState(null);
  const [tz, setTz] = useState(userBrowserTimezone());
  const [viewDate, setViewDate] = useState(startOfMonth(new Date()));
  const [slots, setSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [selectedDay, setSelectedDay] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const [reason, setReason] = useState("");
  const [error, setError] = useState(null);
  const timesRef = useRef(null);

  // v2.87 polish — smooth-scroll the times pane into view when a date is
  // tapped on mobile. Desktop has the times pane next to the calendar so no
  // scroll needed there.
  const scrollToTimesOnMobile = () => {
    if (typeof window === "undefined") return;
    if (window.innerWidth >= 1024) return; // lg breakpoint
    requestAnimationFrame(() => {
      timesRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  };

  // Load the manage payload (status, slug, allow_*, duration, etc.).
  useEffect(() => {
    api.get(`/booking/public/manage/${token}`, { params: { action } })
      .then((r) => setInfo(r.data))
      .catch(() => setError("This link is invalid or has expired."));
  }, [token, action]);

  // Fetch availability for the visible month. Mirrors the public booking
  // page's slot-fetching loop — same endpoint, same params.
  useEffect(() => {
    if (action !== "reschedule" || !info?.slug) return;
    const monthStart = startOfMonth(viewDate);
    const monthEnd = startOfMonth(addMonths(viewDate, 1));
    const start = monthStart.toISOString();
    const end = monthEnd.toISOString();
    setLoadingSlots(true);
    api.get(`/booking/public/links/${info.slug}/availability`, {
      params: { start, end, invitee_timezone: tz },
    })
      .then((r) => setSlots(r.data.slots || []))
      .catch(() => setSlots([]))
      .finally(() => setLoadingSlots(false));
  }, [action, info?.slug, viewDate, tz]);

  const grid = useMemo(() => buildMonthGrid(viewDate, tz), [viewDate, tz]);
  const today = isoDay(new Date(), tz);

  // Group available slots by ISO day in the visitor's timezone.
  const slotsByDay = useMemo(() => {
    const m = new Map();
    for (const s of slots) {
      const key = isoDay(new Date(s.start_utc), tz);
      const list = m.get(key) || [];
      list.push(s);
      m.set(key, list);
    }
    return m;
  }, [slots, tz]);

  const monthSlots = slots;
  const canPrev = startOfMonth(viewDate) > startOfMonth(new Date());
  const canNext = true;

  const handleReschedule = async () => {
    if (!selectedSlot) return;
    setBusy(true);
    try {
      const r = await api.post(`/booking/public/reschedule/${token}`, {
        start_time_utc: selectedSlot.start_utc,
        end_time_utc: selectedSlot.end_utc,
        invitee_timezone: tz,
      });
      setDone({ kind: "reschedule", booking: r.data });
    } catch (e) {
      toast.error(formatApiError(e) || "Reschedule failed");
    } finally {
      setBusy(false);
    }
  };

  const handleCancel = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/booking/public/cancel/${token}`, {
        reason,
      });
      setDone({ kind: "cancel", booking: r.data });
    } catch (e) {
      toast.error(formatApiError(e) || "Cancel failed");
    } finally {
      setBusy(false);
    }
  };

  // --- Render states ---------------------------------------------------

  if (error) {
    return (
      <PublicShell>
        <div className="rounded-2xl p-10 max-w-md mx-auto text-center bg-white border border-[#1a1a1f]/8" data-testid="manage-error">
          <AlertCircle className="w-10 h-10 text-[#c43838] mx-auto mb-3" />
          <p className="bloq-headline text-xl">{error}</p>
        </div>
      </PublicShell>
    );
  }

  if (!info) {
    return (
      <PublicShell>
        <div className="text-center text-sm text-[#82828c]">Loading…</div>
      </PublicShell>
    );
  }

  if (done) {
    return (
      <PublicShell>
        <div className="rounded-2xl p-10 max-w-md mx-auto text-center bg-white border border-[#1a1a1f]/8" data-testid="manage-done">
          <p className="bloq-headline text-2xl mb-3">
            {done.kind === "cancel" ? "Booking canceled" : "Booking rescheduled"}
          </p>
          <p className="text-base text-[#4a4a52]">
            {done.kind === "cancel"
              ? "A cancellation confirmation has been emailed to you."
              : "A new confirmation has been emailed to you with the updated time."}
          </p>
        </div>
      </PublicShell>
    );
  }

  // --- Cancel view (unchanged copy, just wrapped in the logo-free shell)
  if (action === "cancel") {
    if (!info.allow_cancel) {
      return (
        <PublicShell>
          <div className="rounded-2xl p-10 max-w-md mx-auto text-center bg-white border border-[#1a1a1f]/8">
            <p className="bloq-headline text-xl">Cancellation isn't allowed for this booking.</p>
            <p className="text-base text-[#82828c] mt-2">Please contact your host directly.</p>
          </div>
        </PublicShell>
      );
    }
    return (
      <PublicShell>
        <div className="rounded-2xl p-8 max-w-xl mx-auto bg-white border border-[#1a1a1f]/8" data-testid="booking-manage-cancel">
          <p className="bloq-headline text-2xl mb-2">Cancel your booking</p>
          <p className="text-base text-[#4a4a52] mb-5">
            <strong>{info.title}</strong> · {formatLongDateInTz(info.start_time_utc, info.invitee_timezone)}
          </p>
          <label className="text-sm uppercase tracking-widest text-[#82828c] font-medium block mb-1.5">Reason (optional)</label>
          <textarea
            rows={3}
            className="bloq-input text-base"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            data-testid="manage-cancel-reason"
          />
          <div className="flex items-center justify-end gap-3 mt-5">
            <button
              type="button"
              onClick={handleCancel}
              disabled={busy}
              className="bloq-btn-primary bg-[#c43838] hover:bg-[#a82929]"
              data-testid="manage-cancel-submit"
            >
              {busy ? "Canceling…" : "Cancel booking"}
            </button>
          </div>
        </div>
      </PublicShell>
    );
  }

  // --- Reschedule view (new calendar-grid design, matches PublicBookingV2)
  if (!info.allow_reschedule) {
    return (
      <PublicShell>
        <div className="rounded-2xl p-10 max-w-md mx-auto text-center bg-white border border-[#1a1a1f]/8">
          <p className="bloq-headline text-xl">Rescheduling isn't allowed for this booking.</p>
          <p className="text-base text-[#82828c] mt-2">Please contact your host directly.</p>
        </div>
      </PublicShell>
    );
  }

  // If the booking has no slug (custom-AT manual booking), show a
  // friendly message instead of an empty grid.
  if (!info.slug) {
    return (
      <PublicShell>
        <div className="rounded-2xl p-10 max-w-md mx-auto text-center bg-white border border-[#1a1a1f]/8" data-testid="manage-no-slug">
          <p className="bloq-headline text-xl">This booking can't be self-rescheduled.</p>
          <p className="text-base text-[#82828c] mt-2">Please reply to your confirmation email or contact your host directly.</p>
        </div>
      </PublicShell>
    );
  }

  return (
    <PublicShell>
      <div className="max-w-6xl mx-auto" data-testid="booking-manage-reschedule">
        <div className="grid lg:grid-cols-[300px_1fr] gap-6">
          {/* LEFT — Current booking summary */}
          <aside className="rounded-2xl p-7 bg-white border border-[#1a1a1f]/8 h-fit lg:sticky lg:top-6" data-testid="manage-aside">
            <p className="text-xs uppercase tracking-widest text-[#82828c] mb-2">Reschedule</p>
            <p className="bloq-headline text-2xl">{info.title}</p>

            <div className="flex flex-col gap-3 mt-5 text-base text-[#4a4a52]">
              {info.duration_minutes && (
                <span className="inline-flex items-center gap-2">
                  <Clock className="w-5 h-5 text-[#005e7a]" />
                  {formatDuration(info.duration_minutes)}
                </span>
              )}
              <span className="inline-flex items-start gap-2">
                <UserIcon className="w-5 h-5 text-[#005e7a] mt-0.5 flex-shrink-0" />
                <span className="flex flex-col">
                  <span>{info.invitee_name}</span>
                  <span className="text-sm text-[#82828c] inline-flex items-center gap-1">
                    <Mail className="w-3.5 h-3.5" /> {info.invitee_email}
                  </span>
                </span>
              </span>
            </div>

            <div className="mt-5 pt-5 border-t border-[#1a1a1f]/06">
              <p className="text-xs uppercase tracking-widest text-[#82828c] mb-1.5">Current time</p>
              <p className="text-base text-[#1a1a1f]">
                {formatLongDateInTz(info.start_time_utc, info.invitee_timezone)} at {formatTime12hInTz(info.start_time_utc, info.invitee_timezone)}
              </p>
            </div>

            {selectedSlot && (
              <div className="mt-5 pt-5 border-t border-[#1a1a1f]/06" data-testid="manage-new-time-preview">
                <p className="text-xs uppercase tracking-widest text-[#005e7a] mb-1.5">New time</p>
                <p className="text-base text-[#1a1a1f]">
                  {formatLongDateInTz(selectedSlot.start_utc, tz)} at {formatTime12hInTz(selectedSlot.start_utc, tz)}
                </p>
                <button
                  type="button"
                  onClick={handleReschedule}
                  disabled={busy}
                  className="bloq-btn-primary w-full mt-4"
                  data-testid="manage-reschedule-submit"
                >
                  {busy ? "Confirming…" : "Confirm new time"}
                </button>
              </div>
            )}
          </aside>

          {/* RIGHT — Calendar + Times pane (same markup as PublicBookingV2) */}
          <section className="rounded-2xl bg-white border border-[#1a1a1f]/8 overflow-hidden" data-testid="manage-scheduler">
            <div className="grid lg:grid-cols-[1fr_280px] lg:items-stretch">
              {/* Calendar pane */}
              <div className="p-7 lg:border-r border-[#1a1a1f]/06" data-testid="manage-calendar">
                <div className="flex items-center justify-between gap-4 mb-6">
                  <button
                    onClick={() => canPrev && setViewDate((d) => addMonths(d, -1))}
                    disabled={!canPrev}
                    className="w-10 h-10 rounded-full inline-flex items-center justify-center hover:bg-[#f5efe6] disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                    data-testid="manage-prev-month"
                    aria-label="Previous month"
                  >
                    <ChevronLeft className="w-5 h-5" />
                  </button>
                  <p className="bloq-headline text-2xl flex-1 text-center">
                    {viewDate.toLocaleDateString("en-US", { month: "long", year: "numeric" })}
                  </p>
                  <button
                    onClick={() => canNext && setViewDate((d) => addMonths(d, 1))}
                    disabled={!canNext}
                    className="w-10 h-10 rounded-full inline-flex items-center justify-center hover:bg-[#f5efe6] disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                    data-testid="manage-next-month"
                    aria-label="Next month"
                  >
                    <ChevronRight className="w-5 h-5" />
                  </button>
                </div>

                <div className="grid grid-cols-7 gap-1 text-xs uppercase tracking-widest text-[#82828c] mb-2">
                  {["S", "M", "T", "W", "T", "F", "S"].map((d, i) => (
                    <div key={i} className="text-center py-1">{d}</div>
                  ))}
                </div>

                <div className="grid grid-cols-7 gap-1.5">
                  {grid.map((day, idx) => {
                    const daySlots = slotsByDay.get(day.key) || [];
                    const isPast = day.key < today;
                    const disabled = !day.inMonth || isPast || daySlots.length === 0;
                    const isSelected = selectedDay === day.key;
                    const isToday = day.key === today;
                    return (
                      <button
                        key={idx}
                        onClick={() => { if (!disabled) { setSelectedDay(day.key); setSelectedSlot(null); scrollToTimesOnMobile(); } }}
                        disabled={disabled}
                        className={`relative aspect-square rounded-lg text-base font-medium transition-all
                          ${isSelected ? "bg-[#005e7a] text-white shadow-md scale-105"
                            : disabled ? "text-[#c4c4cb] cursor-not-allowed"
                            : "text-[#1a1a1f] hover:bg-[#005e7a]/10 hover:text-[#005e7a] bg-[#f5efe6]/50"}
                          ${!day.inMonth ? "opacity-30" : ""}
                        `}
                        data-testid={`manage-day-${day.key}`}
                      >
                        <span>{day.date.getDate()}</span>
                        {isToday && !isSelected && (
                          <span className="absolute bottom-1 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full bg-[#005e7a]" />
                        )}
                      </button>
                    );
                  })}
                </div>

                <p className="text-sm text-[#82828c] mt-6 text-center">
                  {loadingSlots ? "Loading availability…" : (
                    monthSlots.length === 0
                      ? `No times available in ${viewDate.toLocaleDateString("en-US", { month: "long" })}. Try another month.`
                      : ""
                  )}
                </p>
              </div>

              {/* Times pane — see PublicBookingV2.jsx for the mobile fix
                  rationale (CHANGELOG v2.87). On lg+ the inner column is
                  absolute-positioned to attach to the calendar's stretched
                  height; on mobile/tablet it falls back to normal flex flow
                  so the times render below the calendar instead of collapsing
                  to 0px. */}
              <div ref={timesRef} className="bg-[#fafafa] relative" data-testid="manage-times">
                <div className="flex flex-col lg:absolute lg:inset-0">
                  <div className="p-5 border-b border-[#1a1a1f]/06 shrink-0">
                    <label className="text-xs uppercase tracking-widest text-[#82828c] font-medium block mb-1.5 inline-flex items-center gap-1.5">
                      <Globe className="w-3.5 h-3.5" /> Your timezone
                    </label>
                    <Select value={tz} onValueChange={setTz}>
                      <SelectTrigger className="w-full text-sm h-10" data-testid="manage-tz">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="max-h-72">
                        {COMMON_TIMEZONES.map((z) => (
                          <SelectItem key={z} value={z}>{tzLabelWithOffset(z)}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="p-5 pb-2 shrink-0">
                    {selectedDay ? (
                      <>
                        <p className="text-base font-medium text-[#1a1a1f]">
                          {formatLongDateInTz(`${selectedDay}T12:00:00Z`, tz)}
                        </p>
                        <p className="text-xs uppercase tracking-widest text-[#82828c] mt-1">
                          {(slotsByDay.get(selectedDay) || []).length} time{(slotsByDay.get(selectedDay) || []).length === 1 ? "" : "s"} available
                        </p>
                      </>
                    ) : (
                      <p className="text-base text-[#82828c]">Select a date to see times.</p>
                    )}
                  </div>

                  {selectedDay && (
                    <div className="max-h-[60vh] overflow-y-auto lg:flex-1 lg:max-h-none px-5 pb-5 space-y-2 lg:min-h-0">
                      {(slotsByDay.get(selectedDay) || []).map((s) => {
                        const local = formatTime12hInTz(s.start_utc, tz);
                        const sel = selectedSlot?.start_utc === s.start_utc;
                        return (
                          <button
                            key={s.start_utc}
                            onClick={() => setSelectedSlot(s)}
                            className={`w-full px-4 py-3 rounded-lg text-base font-medium border transition-all
                              ${sel
                                ? "bg-[#005e7a] text-white border-[#005e7a]"
                                : "bg-white text-[#1a1a1f] border-[#1a1a1f]/10 hover:border-[#005e7a] hover:text-[#005e7a]"}
                            `}
                            data-testid={`manage-slot-${s.start_utc}`}
                          >
                            {local}
                          </button>
                        );
                      })}
                      {(slotsByDay.get(selectedDay) || []).length === 0 && (
                        <p className="text-sm text-[#82828c] py-4 text-center">No times available on this date.</p>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </section>
        </div>
      </div>
    </PublicShell>
  );
}
